import { useEffect, useState } from 'react';

/**
 * A typed React hook for persisting state to localStorage.
 *
 * @template T - The type of the stored value.
 * @param key - The localStorage key to store the value under.
 * @param initialValue - The initial value to use if nothing is stored.
 * @returns A tuple containing the stored value and a setter function.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
  // Lazy initializer to read from localStorage only once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`useLocalStorage: Error reading key "${key}"`, error);
      return initialValue;
    }
  });

  // Update localStorage whenever the value changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
      // Notify other hook instances in the same tab
      window.dispatchEvent(
        new CustomEvent('local-storage', {
          detail: { key, value: storedValue },
        }),
      );
    } catch (error) {
      console.warn(`useLocalStorage: Error setting key "${key}"`, error);
    }
  }, [key, storedValue]);

  // Keep hook state in sync when localStorage changes elsewhere
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const readValueFromStorage = () => {
      try {
        const item = window.localStorage.getItem(key);
        return item ? (JSON.parse(item) as T) : initialValue;
      } catch (error) {
        console.warn(`useLocalStorage: Error reading key "${key}"`, error);
        return initialValue;
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      setStoredValue(readValueFromStorage());
    };

    const handleCustomEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ key: string; value: T }>;
      if (customEvent.detail?.key !== key) return;
      setStoredValue(customEvent.detail.value);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('local-storage', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('local-storage', handleCustomEvent);
    };
  }, [key, initialValue]);

  // Setter function that supports functional updates
  const setValue = (value: T | ((val: T) => T)) => {
    setStoredValue((prev) => (value instanceof Function ? value(prev) : value));
  };

  return [storedValue, setValue];
}
